import {
  renderToolFieldMappings,
  categoryFieldOverrides,
} from '../../CCM/models/CCM_TOOL_RENDER_FIELDS'
import { SupportedTools, UnsafeTemplateError } from '../../interfaces'

import type { AstNode, AstGroup, AstPredicate, ValueOrList } from './types'

/* ---------- Core types ---------- */
export type RenderOut =
  | { kind: 'text'; text: string }
  | { kind: 'lines'; lines: string[] }

export type PredicateFormatter = (
  p: AstPredicate,
  renderContext: RenderContext,
) => string

// make makeRenderer return a function type
export type RendererFn = (ast: AstNode) => string
export type RendererWithMeta = RendererFn

export interface GroupStrategy {
  emitGroup(
    group: AstGroup,
    emitNode: (n: AstNode) => RenderOut,
    renderContext: RenderContext,
    isRoot: boolean,
  ): RenderOut
}

// Build our renderContext variable that will allow us to render the query based on configurations in each renderer
// These are the currently configured customizations per tool -- can be expanded as necessary to account for additional tool variations
export interface RenderContext {
  tool: SupportedTools
  mapField(canonical: string): string // mapped + sanitized identifier
  fieldExists(canonical: string): boolean // true if field has an explicit CCM mapping for this tool
  wrapField(fieldIdent: string): string // e.g., Eta wrapper or no-op
  quote(s: string): string // value quoting
  list(values: string[]): string // IN(...) builder
  andGlue: string // ' AND '
  orGlue: string // ' OR '
  regexFlags: string // e.g., 'i' -- outlier for logscale and potential future support accountability
  alwaysQuote: boolean // always quote values -- even integers and types
  linePrefix?: string // e.g., '| ' for pipe tools -- currently outlier for logscale and enable support for future tools like Cortex XDR
  // Core conditional operators that would require wildcarding -- can be expanded if others are identified
  wildcard: {
    contains(v: string): string
    startswith(v: string): string
    endswith(v: string): string
    equals(v: string): string
  }
}

/* ---------- Defaults & helpers ---------- */
// Setting default behavior to enable renderers to only configure exception behavior instead of configuring all renderers to address each configuration
export function buildRenderContext(
  tool: SupportedTools,
  opts?: Partial<RenderContext>,
  category?: string,
): RenderContext {
  const map = renderToolFieldMappings[tool] ?? {}
  const catOverrides = category
    ? (categoryFieldOverrides[tool]?.[category] ?? {})
    : {}
  const def: RenderContext = {
    tool,
    mapField: (c) =>
      (catOverrides[c] ?? map[c] ?? c).replace(/[^\w.\-#@()]/g, ''),
    fieldExists: (c) => c in catOverrides || c in map,
    wrapField: (f) => f,
    quote: (s) => `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`,
    list: (vals) =>
      `("${vals.map((v) => v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')).join('","')}")`,
    andGlue: ' AND ',
    orGlue: ' OR ',
    regexFlags: 'i',
    alwaysQuote: true,
    linePrefix: undefined,
    wildcard: {
      contains: (v) => `*${v}*`,
      startswith: (v) => `${v}*`,
      endswith: (v) => `*${v}`,
      equals: (v) => v,
    },
  }
  return { ...def, ...opts }
}

// normalize any incoming value into a string array, so renderers will not need to figure out if the value is string or list
export function normalizeList(v: ValueOrList | undefined): string[] {
  if (v == null) return ['']
  if (Array.isArray(v)) return v.map(scalarToString)
  return [scalarToString(v)]
}
/** Eta tag delimiters — must match the Eta.configure() call in tools/base.ts. */
const ETA_TAG_OPEN = '{{!'
const ETA_TAG_CLOSE = '!}}'

// canonical scalar to string conversion
export function scalarToString(v: any): string {
  const s = v instanceof Date ? v.toISOString() : String(v ?? '')
  // This value is concatenated into a query string that is later
  // compiled by Eta (renderContentTemplate), so a value carrying template
  // delimiters is executable JavaScript. Reject rather than strip: stripping
  // is defeatable because the stripped result can itself contain delimiters
  // (`{{{{!!= 91*7 !!}}}}` -> `{{!= 91*7 !}}`), and nesting deeper defeats any
  // fixed number of passes. Legitimate query values never contain these
  // sequences — note plain braces are fine, so regex quantifiers like
  // `[a-f0-9]{32}` still pass. See tuning-ssti.ava.spec.js.
  if (s.includes(ETA_TAG_OPEN) || s.includes(ETA_TAG_CLOSE)) {
    throw new UnsafeTemplateError(
      'Query value contains Eta template delimiters',
    )
  }
  return s
}

/**
 * Render Boundary): a tuning query is machine-generated from
 * user-supplied filters and then compiled by Eta, so the finished string must
 * be checked before it is rendered — not just the inputs that built it. The
 * only tag it may legitimately contain is the field lookup emitted by
 * etaFieldLookup(); anything else means some path produced markup we did not
 * intend, and that path executes as JavaScript.
 *
 * Keep SAFE_FIELD_LOOKUP in sync with etaFieldLookup() below — the
 * backreference requires the `??` fallback to equal the lookup key, which is
 * what that function always emits.
 *
 * Note this applies to tuning output only. Authored content templates
 * legitimately contain arbitrary Eta (conditionals, helpers) and are written
 * by trusted content authors, so they must not be passed here.
 */
const SAFE_FIELD_LOOKUP =
  /\{\{!=\s*it\[("(?:[^"\\]|\\.)*")\]\s*\?\?\s*\1\s*!\}\}/g

export function assertOnlyFieldLookups(template: string): void {
  if (template.replace(SAFE_FIELD_LOOKUP, '').includes(ETA_TAG_OPEN)) {
    throw new UnsafeTemplateError(
      'Tuning query contains a template tag that is not a field lookup',
    )
  }
}
/**
 * SECURITY: wrap an (already field-mapped) identifier as an Eta lookup that can
 * only resolve to a context key — never evaluate as an arbitrary expression.
 *
 * The naive `{{!=${f}!}}` treats `f` as a JS expression, so a crafted field
 * name like `process.env` or `process.exit()` executes when Eta renders the
 * template (SSTI). Looking the field up as a string key on the context object
 * (`it["process.env"]`) can only ever read a property that isn't there (=>
 * undefined), and the `?? key` fallback preserves the prior passthrough intent
 * of emitting the field name when no customer mapping exists.
 * See tuning-ssti.ava.spec.js.
 */
export function etaFieldLookup(fieldIdent: string): string {
  const key = JSON.stringify(fieldIdent)
  return `{{!= it[${key}] ?? ${key} !}}`
}

// Testing for logscale regex escape input -- Perl escapes
export function escapeRx(s: string): string {
  return String(s).replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')
}
export const alt = (values: string[]) => values.map(escapeRx).join('|')
export const altAnchored = (values: string[], anchor: '^' | '$') =>
  anchor === '^'
    ? `${values.map((v) => `^${escapeRx(v)}`).join('|')}`
    : `${values.map((v) => `${escapeRx(v)}$`).join('|')}`

/* ---------- Core renderer engine ---------- */
export function makeRenderer(config: {
  tool: SupportedTools
  groupStrategy: GroupStrategy
  formatPredicate: PredicateFormatter
  renderContext?: Partial<RenderContext>
  finalize?: (out: RenderOut, renderContext: RenderContext) => string // lines/text -> final string
}): RendererWithMeta {
  const fn = ((ast: AstNode): string => {
    // Extract resolved category from the root's datasource directive (if present).
    // Only the first category is used for field-map override selection.
    const dsCat = (ast as AstGroup).directives?.datasource?.category
    const category = Array.isArray(dsCat) ? dsCat[0] : (dsCat ?? undefined)
    const renderContext = buildRenderContext(
      config.tool,
      config.renderContext,
      category,
    )
    const out = emitNode(ast, true)
    return (config.finalize ?? defaultFinalize)(out, renderContext)

    function emitNode(n: AstNode, isRoot = false): RenderOut {
      if (n.kind === 'pred') {
        return { kind: 'text', text: config.formatPredicate(n, renderContext) }
      }
      return config.groupStrategy.emitGroup(
        n,
        (child) => emitNode(child, false),
        renderContext,
        isRoot,
      )
    }
  }) as RendererWithMeta

  return fn
}

// lines -> prefixed join -- text -> as-is
/* v1 working
function defaultFinalize(out: RenderOut, renderContext: RenderContext): string {
  if (out.kind === "text") return out.text;
  const prefix = renderContext.linePrefix ?? "";
  return out.lines.map(l => prefix + l).join("\n");
}
*/
// V2 -- addresses the missing pipe and join syntax when utilizing OR as a root condition
function defaultFinalize(out: RenderOut, renderContext: RenderContext): string {
  const prefix = renderContext.linePrefix ?? ''
  if (out.kind === 'text') return prefix ? prefix + out.text : out.text
  return out.lines.map((l) => prefix + l).join('\n')
}

/* ---------- Stock group strategies ---------- */

// Inline strategy: join with AND/OR (with correct case), no parentheses at root, parentheses nested
export const InlineGroups: GroupStrategy = {
  emitGroup(group, emitNode, renderContext, isRoot): RenderOut {
    const parts = group.children
      .map(emitNode)
      .map((o) => flattenText(o, renderContext.andGlue))
      .filter(Boolean) as string[]
    const glue =
      group.logic === 'and' ? renderContext.andGlue : renderContext.orGlue
    if (!parts.length) return { kind: 'text', text: '' }
    if (isRoot) return { kind: 'text', text: parts.join(glue) } // no root parens
    if (parts.length === 1) return { kind: 'text', text: parts[0] }
    return { kind: 'text', text: `(${parts.join(glue)})` }
  },
}

// Pipe-per-AND at root -- nested groups inline with parentheses -- CrowdStrike LogScale, KQL and Palo Alto handling
export const PipeAndGroups: GroupStrategy = {
  emitGroup(group, emitNode, renderContext, isRoot): RenderOut {
    // Collapse any RenderOut to a single inline string
    const inlineOf = (out: RenderOut) =>
      out.kind === 'text'
        ? out.text
        : `(${out.lines.join(renderContext.andGlue)})`

    if (group.logic === 'and') {
      if (isRoot) {
        // ROOT AND: one line per child
        const lines: string[] = []
        for (const child of group.children) {
          const out = emitNode(child)
          if (out.kind === 'lines') lines.push(...out.lines)
          else lines.push(out.text)
        }
        return { kind: 'lines', lines }
      }

      // NESTED AND: inline, parenthesized if multiple parts
      const parts = group.children
        .map((c) => inlineOf(emitNode(c)))
        .filter(Boolean) as string[]
      if (!parts.length) return { kind: 'text', text: '' }
      if (parts.length === 1) return { kind: 'text', text: parts[0] }
      return { kind: 'text', text: `(${parts.join(renderContext.andGlue)})` }
    }

    // OR group (root or nested): inline, parenthesized when multiple parts
    const parts = group.children
      .map((c) => inlineOf(emitNode(c)))
      .filter(Boolean) as string[]
    if (!parts.length) return { kind: 'text', text: '' }
    const line =
      parts.length === 1 ? parts[0] : `(${parts.join(renderContext.orGlue)})`
    return { kind: 'text', text: line }
  },
}

function flattenText(out: RenderOut, glue: string): string | undefined {
  if (out.kind === 'text') return out.text || undefined
  // lines inside inline context -- join with AND to preserve semantics
  return out.lines.length ? `(${out.lines.join(glue)})` : undefined
}
